import { ref, reactive, onBeforeMount, watch } from "@vue/composition-api";
import router from "@/router";
import store from "@/store";
import useModal from "@/@core/components/base-modal/hooks/useModal";
import useValidationModal from "@/@core/components/base-modal/hooks/useValidationModal";
import { categoryModel } from "@/models/privilege/privilege-category";
import usePagination from "@/views/components/pagination/usePagination";
import useFilterHeaderTable from "@/views/components/filter-header-table/useFilterHeaderTable";

export default function usePrivilegeSubCategory(props) {
  const { getSubCategoryList, fetchReorderCategory, fetchDeleteCategory } = categoryModel();
  const { pagination, onPageChange, onFilterEntity } = usePagination();
  const {
    filters,
    ordering,
    onSetValueSearch,
    onSelectFilter,
  } = useFilterHeaderTable();
  const { openModal, closeModal } = useModal();
  const { openDefaultErrorModal, openErrorModal, openWarningModal } = useValidationModal();
  const keyChange = ref(0);
  const currentIndex = ref(null);
  const currentIdSelect = ref(null);
  const selectSubCate = ref(null);
  const tableModeReorder = ref(false);
  const loading = reactive({
    tableData: false,
    tableOverlay: false,
  });

  const fields = [
    { key: "seq", label: "No." },
    { key: "alt_reference", label: "Reference" },
    { key: "category_image_url", label: "Icon/Image" },
    { key: "category_sub_image_url", label: "Sub Icon/Image" },
    { key: "name_en", label: "Name (En)" },
    { key: "name_th", label: "Name (Th)" },
    // { key: "item_count", label: "Item (Privilege)" },
    // { key: "tags", label: "Tags" },
    { key: "status", label: "Status" },
    { key: "actions", label: "" },
  ];

  const formStatus = reactive({
    validFrom: null,
    validThrough: null,
    status: null,
  });

  const privilegeCategoryActionList = reactive({
    edit: {
      label: "Edit",
      value: 1,
      icon: "EditIcon",
      display: true,
    },
    reOrder: {
      label: "Reorder",
      value: 2,
      icon: "ReorderIcon",
      display: false,
    },
    delete: {
      label: "Delete",
      value: 3,
      icon: "DeleteIcon",
      display: true,
    },
  });

  const statusOption = ref([
    { label: "Active", value: 1 },
    { label: "Inactive", value: 2 },
  ]);

  const privilegeCategoryTable = reactive({
    payload: [],
  });

  const modalType = reactive({
    reorder: false,
    createSubCategory: false,
  });

  const resolveMappingTableAction = (type, item) => {
    switch (type) {
      case 1:
        selectSubCate.value = item.item.category_id
        onOpenModalSubCategory();
        return;
      case 2:
        modalType.reorder = true;
        openReorderModal(item.item);
      case 3:
        deleteCategory(item.item);
      default:
    }
  };

  const createSubCate = () => {
    selectSubCate.value = null;
    onOpenModalSubCategory();
  }

  const onOpenModalSubCategory = () => {
    modalType.createSubCategory = true;
    openModal();
  };

  const onCloseModalPrivilege = () => {
    closeModal();
    modalType.reorder = false;
    modalType.createSubCategory = false;
  };

  const onClickReorder = () => {
    privilegeCategoryActionList.reOrder.display = !privilegeCategoryActionList
      .reOrder.display;
    tableModeReorder.value = !tableModeReorder.value;
    sortableRowOptions.value.disabled = !sortableRowOptions.value.disabled;
  };

  const handleRowChange = (event) => {
    fetchReorderList(
      privilegeCategoryTable.payload[event.oldDraggableIndex].category_id,
      privilegeCategoryTable.payload[event.oldDraggableIndex].seq,
      privilegeCategoryTable.payload[event.newDraggableIndex].seq
    );
    privilegeCategoryTable.payload.splice(
      event.newDraggableIndex,
      0,
      privilegeCategoryTable.payload.splice(event.oldDraggableIndex, 1)[0]
    );
    keyChange.value++;
  };

  const fetchReorderList = (id, currentPosition, newPosition) => {
    loading.tableOverlay = true;
    fetchReorderCategory.payload.Category_Id = id;
    fetchReorderCategory.payload.From = currentPosition;
    fetchReorderCategory.payload.To = newPosition;
    store
      .dispatch("api/apiRequest", fetchReorderCategory)
      .then((response) => {
        fetchPrivilegeCategoryList(false);
      })
      .catch((error) => {
        openDefaultErrorModal(error);
        loading.tableOverlay = false;
      });
  };

  const sortableRowOptions = ref({
    chosenClass: "is-selected",
    ghostClass: "sortable-ghost",
    onEnd: handleRowChange,
    disabled: true,
    animation: 300,
    easing: "cubic-bezier(0.22, 1, 0.36, 1)",
  });

  const openReorderModal = (item) => {
    currentIndex.value = item.seq;
    currentIdSelect.value = item.category_id;
    openModal();
  };

  const reorderPrivilege = async (current, newIndex) => {
    fetchReorderList(currentIdSelect.value, currentIndex.value, newIndex);

    keyChange.value++;
    onCloseModalPrivilege();
  };

  const resolveStatusLabel = (status) => {
    switch (status) {
      case 1:
        return "Active";
      case 2:
        return "Inactive";
      default:
        return "Unknown";
    }
  };

  const resolveStatusClass = (status) => {
    switch (status) {
      case 1:
        return "active-icon";
      case 2:
        return "inActive-icon";
      default:
        return "Unknown";
    }
  };

  const onSuccessForm = () => {
    onCloseModalPrivilege();
    fetchPrivilegeCategoryList(false);
  }

  const deleteCategory = (category) => {
    openWarningModal(
      "ลบ Category",
      `ต้องการลบ ${category.name_th} ใช่หรือไม่`,
      () => {
        loading.tableData = true;
        fetchDeleteCategory.payload.Category_Id = category.category_id;
        store
          .dispatch("api/apiRequest", fetchDeleteCategory)
          .then((response) => {
            fetchPrivilegeCategoryList();
          })
          .catch((error) => {
            loading.tableData = false;
            openErrorModal(error);
          })
          .finally(() => {});
      }
    );
  }

  const fetchPrivilegeCategoryList = (showLoading = true) => {
    if (showLoading) {
      loading.tableData = true;
    }
    getSubCategoryList.payload.Page = pagination.currentPage;
    getSubCategoryList.payload.Limit = pagination.perPage;
    getSubCategoryList.payload.Filters = filters;
    getSubCategoryList.payload.Ordering = ordering.value;
    getSubCategoryList.payload.Category_Id = props.CategoryId;
    store
      .dispatch("api/apiRequest", getSubCategoryList)
      .then((response) => {
        privilegeCategoryTable.payload = response.data.payload;
        pagination.rows = response.data.payload.length;
        pagination.totalRows = response.data.filtered_record;
      })
      .catch((err) => {
        openDefaultErrorModal(err);
      })
      .finally(() => {
        loading.tableData = false;
        loading.tableOverlay = false;
      });
  };

  watch(
    pagination,
    () => {
      if (!loading.tableData) {
        fetchPrivilegeCategoryList();
      }
    },
    { deep: true }
  );

  watch(
    [filters, ordering],
    () => {
      pagination.currentPage = 1;

      fetchPrivilegeCategoryList();
    },
    { deep: true }
  );

  onBeforeMount(() => {
    fetchPrivilegeCategoryList();
  });

  return {
    selectSubCate,
    pagination,
    formStatus,
    fields,
    loading,
    statusOption,
    privilegeCategoryTable,
    privilegeCategoryActionList,
    sortableRowOptions,
    keyChange,
    tableModeReorder,
    currentIndex,
    modalType,
    resolveMappingTableAction,
    resolveStatusLabel,
    resolveStatusClass,
    reorderPrivilege,
    onClickReorder,
    reorderPrivilege,
    onCloseModalPrivilege,
    onOpenModalSubCategory,
    onPageChange,
    onFilterEntity,
    onSetValueSearch,
    onSelectFilter,
    createSubCate,
    onSuccessForm
  };
}
